import {
  Add,
  AutoAwesome,
  EditOutlined,
  MoreVert,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import ContentStrategy from "./ContentStrategy";
import ContentStrategyDialog from "./ContentStrategyDialog";
import ContentStrategyGenDialog from "./ContentStrategyGenDialog";

interface ContentStrategy {
  id: string;
  name: string;
  salesNarrative: string;
  targetAudience: string;
  pillars: any[];
  created_date: string;
  updated_date: string;
}

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [strategies, setStrategies] = useState<ContentStrategy[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [strategyForDialog, setStrategyForDialog] =
    useState<ContentStrategy | null>(null);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(
    null
  );

  // Fetch strategies
  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/strategies`, {
        params: {
          companyId,
          page: page + 1, // API uses 1-based pagination
          pageSize,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStrategies(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching strategies:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  // Legacy simple generation - will be replaced by the dialog
  const generateStrategy = async () => {
    setGeneratingStrategy(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/strategy/generate`,
        { companyId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchStrategies();
      eventListener({
        type: "SET_INFO",
        info: "New content strategy generated successfully",
      });
    } catch (error) {
      console.error("Error generating strategy:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setGeneratingStrategy(false);
    }
  };
  
  // Handle completion from the AI generation dialog
  const handleGenerateComplete = () => {
    fetchStrategies();
  };

  // Delete a strategy
  const deleteStrategy = (strategyId: string) => {
    openDialog(
      "Delete Content Strategy",
      "Are you sure you want to delete this content strategy? This action cannot be undone.",
      "Delete",
      async () => {
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/content/strategy/${strategyId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          fetchStrategies();
          eventListener({
            type: "SET_INFO",
            info: "Content strategy deleted successfully",
          });
        } catch (error) {
          console.error("Error deleting strategy:", error);
          eventListener({
            type: "SET_ERROR",
            error: parseError(error),
          });
        }
      }
    );
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle menu clicks
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    strategyId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedStrategy(strategyId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStrategy(null);
  };

  // Handle edit click
  const handleEditClick = () => {
    if (selectedStrategy) {
      setSelectedStrategyId(selectedStrategy);
      handleMenuClose();
    }
  };

  // Handle new strategy dialog
  const handleOpenDialog = () => {
    setStrategyForDialog(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = (refresh: boolean = false) => {
    setDialogOpen(false);
    if (refresh) {
      fetchStrategies();
    }
  };

  // Load strategies on mount and when page/pageSize change
  useEffect(() => {
    if (companyId && currentUser) {
      fetchStrategies();
    }
  }, [companyId, currentUser, page, pageSize]);

  // Get funnel stats for a strategy
  const getFunnelStats = (strategy: ContentStrategy) => {
    if (!strategy.pillars) return { tofu: 0, mofu: 0, bofu: 0 };

    return {
      tofu: strategy.pillars.filter((p) => p.stage === "tofu").length,
      mofu: strategy.pillars.filter((p) => p.stage === "mofu").length,
      bofu: strategy.pillars.filter((p) => p.stage === "bofu").length,
    };
  };

  if (selectedStrategyId) {
    return (
      <Box sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          onClick={() => setSelectedStrategyId(null)}
          sx={{ mb: 2 }}
        >
          Back to Strategies
        </Button>
        <ContentStrategy
          currentUser={currentUser}
          company={company}
          companyId={companyId}
          getToken={getToken}
          eventListener={eventListener}
          openDialog={openDialog}
          currentPlan={currentPlan}
          strategyId={selectedStrategyId}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleOpenDialog}
            sx={{ mr: 1 }}
          >
            New Strategy
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AutoAwesome />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate with AI
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        {loading && <LinearProgress />}
        {!loading && <div style={{ height: 4 }}></div>}
        <IconButton
          onClick={fetchStrategies}
          style={{ float: "right", marginRight: 5 }}
        >
          <RefreshOutlined />
        </IconButton>
        <TableContainer data-tutorial="content-pillars">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Content Pillars</TableCell>
                <TableCell>Funnel Coverage</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography sx={{ py: 2 }}>
                      No content strategies found. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                strategies.map((strategy) => {
                  const funnelStats = getFunnelStats(strategy);
                  return (
                    <TableRow
                      key={strategy.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedStrategyId(strategy.id)}
                      hover
                    >
                      <TableCell>
                        <Typography variant="subtitle1">
                          {strategy.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {strategy.pillars?.length || 0} pillars
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                        >
                          <Chip
                            size="small"
                            label={`TOFU: ${funnelStats.tofu}`}
                            sx={{ bgcolor: "#4caf50", color: "white" }}
                          />
                          <Chip
                            size="small"
                            label={`MOFU: ${funnelStats.mofu}`}
                            sx={{ bgcolor: "#ff9800", color: "white" }}
                          />
                          <Chip
                            size="small"
                            label={`BOFU: ${funnelStats.bofu}`}
                            sx={{ bgcolor: "#f44336", color: "white" }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {strategy.created_date
                          ? moment(strategy.created_date).format("MMM D, YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {strategy.updated_date
                          ? moment(strategy.updated_date).format("MMM D, YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, strategy.id);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <EditOutlined fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedStrategy) {
              deleteStrategy(selectedStrategy);
              handleMenuClose();
            }
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ContentStrategyDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        strategy={strategyForDialog}
      />
      
      {/* AI Generation Dialog */}
      <ContentStrategyGenDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        companyId={companyId}
        company={company}
        getToken={getToken}
        eventListener={eventListener}
        onComplete={handleGenerateComplete}
      />
    </Box>
  );
};
